import type { CmsPage as CmsContent } from '../../../cms/types'
import { API_BASE } from '../../constants'
import { fetchWithPolicy } from '../../../shared/api/fetchPolicy'
import type { ShopLocale } from '../../../i18n/shopLocale'
import {
  buildCanonicalPathFromCmsSlug,
  buildDefaultSeoAlternates,
  buildSeoAlternatesFromRawTranslations,
  dedupeSeoAlternates,
} from '../../seo/localePolicy'

export const fetchPageResponse = async ({
  pageIdOrSlug,
  locale,
  signal,
}: {
  pageIdOrSlug: string
  locale: ShopLocale
  signal: AbortSignal
}) =>
  fetchWithPolicy({
    request:
      pageIdOrSlug === '/'
        ? `${API_BASE}/pages?homepage=1&locale=${encodeURIComponent(locale)}`
        : `${API_BASE}/pages/${encodeURIComponent(pageIdOrSlug)}?locale=${encodeURIComponent(locale)}`,
    init: { signal, cache: 'no-store' },
    retries: 0,
  })

export const attachPageSeoAlternates = async ({
  page,
  signal,
}: {
  page: CmsContent
  signal: AbortSignal
}) => {
  const pageIdentifier = page._id || page.slug
  const fallbackPath = buildCanonicalPathFromCmsSlug(page.slug)
  const currentAlternate = [{ locale: page.locale, path: fallbackPath }]
  if (!pageIdentifier) {
    page.seoAlternates = dedupeSeoAlternates(currentAlternate, fallbackPath)
    return
  }

  const translationsResponse = await fetchWithPolicy({
    request: `${API_BASE}/pages/${encodeURIComponent(pageIdentifier)}/translations?locale=${encodeURIComponent(page.locale)}`,
    init: { signal, cache: 'no-store' },
    retries: 0,
  })
  if (!translationsResponse.ok) {
    page.seoAlternates = dedupeSeoAlternates(currentAlternate, fallbackPath)
    return
  }
  const payload = (await translationsResponse.json()) as { translations?: unknown }
  const translationAlternates = buildSeoAlternatesFromRawTranslations(payload.translations)
  const alternates = dedupeSeoAlternates([...translationAlternates, ...currentAlternate], fallbackPath)
  page.seoAlternates = alternates.length ? alternates : buildDefaultSeoAlternates(fallbackPath)
}
